import React from 'react'
import { createRoot } from 'react-dom/client'
import { PrescriptionPrintView } from '../components/PrescriptionPrintView'
import { Prescription } from '../types/prescription'
import { Patient } from '../types/patient'
import { User } from '../types/user'
import { PracticeLocation } from '../types/practiceLocation'
import '../components/PrescriptionPrintView.css'

/**
 * Print prescription using React component in a new window
 */
export function printPrescription(
  prescription: Prescription,
  patient: Patient,
  prescriber: User,
  practiceLocation?: PracticeLocation,
  logoUrl?: string | null,
): void {
  // Create a new window for printing
  const printWindow = window.open('', '_blank', 'width=800,height=600')
  if (!printWindow) {
    alert('Please allow popups to print prescriptions')
    return
  }

  // Write the HTML structure
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <base href="${typeof window !== 'undefined' ? `${window.location.origin}/` : '/'}" />
        <title>Prescription - ${prescription.prescriptionId}</title>
        <meta charset="utf-8">
        <style>
          body {
            margin: 0;
            padding: 0;
          }
        </style>
      </head>
      <body>
        <div id="prescription-root"></div>
      </body>
    </html>
  `)
  printWindow.document.close()

  // Wait for the document to be ready
  const checkReady = setInterval(() => {
    if (printWindow.document.readyState === 'complete') {
      clearInterval(checkReady)
      
      // Create a style element and inject CSS
      const styleElement = printWindow.document.createElement('style')
      styleElement.textContent = getPrintStylesCSS()
      printWindow.document.head.appendChild(styleElement)

      // Render the React component
      const rootElement = printWindow.document.getElementById('prescription-root')
      if (rootElement) {
        const root = createRoot(rootElement)
        root.render(
          React.createElement(PrescriptionPrintView, {
            prescription,
            patient,
            prescriber,
            practiceLocation,
            logoUrl,
          })
        )

        // Wait a bit for React to render, then print
        setTimeout(() => {
          printWindow.print()
          // Close window after printing (optional)
          // printWindow.close()
        }, 500)
      }
    }
  }, 50)
}

/**
 * Get CSS styles as a string
 * This reads the CSS from the PrescriptionPrintView.css file
 * In a real implementation, you might want to import this differently
 */
function getPrintStylesCSS(): string {
  // For now, return the CSS inline
  // In production, you might want to bundle this differently
  return `
    /* Prescription Print View Styles */
    .prescription-print-view {
      max-width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 20mm;
      background: white;
      font-family: 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.4;
      color: #000;
    }

    .practice-print-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 1px solid #ddd;
    }

    .practice-print-header--logo-only {
      justify-content: flex-end;
    }

    .practice-logo-container {
      flex-shrink: 0;
      text-align: right;
    }

    .practice-logo {
      max-height: 60px;
      max-width: 180px;
      object-fit: contain;
    }

    .practice-header-text {
      flex: 1;
      min-width: 0;
      text-align: left;
    }

    .practice-name {
      font-size: 16pt;
      font-weight: bold;
      margin-bottom: 8px;
      color: #000;
    }

    .practice-address {
      font-size: 10pt;
      color: #555;
      line-height: 1.5;
    }

    .practice-contact {
      font-size: 10pt;
      color: #555;
      line-height: 1.5;
      margin-top: 4px;
    }

    .prescription-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #000;
      padding-bottom: 10px;
      margin-bottom: 20px;
      margin-top: 15px;
    }

    .prescription-title {
      font-size: 18pt;
      font-weight: bold;
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .prescription-date {
      font-size: 11pt;
      font-weight: normal;
    }

    .prescription-body {
      margin-bottom: 30px;
    }

    .prescription-section {
      margin-bottom: 25px;
      page-break-inside: avoid;
    }

    .section-title {
      font-size: 13pt;
      font-weight: bold;
      margin: 0 0 12px 0;
      border-bottom: 1px solid #333;
      padding-bottom: 5px;
      text-transform: uppercase;
    }

    .patient-info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px 30px;
    }

    .info-item {
      display: flex;
      gap: 10px;
    }

    .info-item.full-width {
      grid-column: 1 / -1;
    }

    .info-label {
      font-weight: bold;
      min-width: 120px;
    }

    .info-value {
      flex: 1;
    }

    .medications-section {
      page-break-inside: avoid;
    }

    .medications-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      font-size: 10pt;
    }

    .medications-table thead {
      background-color: #f5f5f5;
      border-bottom: 2px solid #000;
    }

    .medications-table th {
      padding: 8px 6px;
      text-align: left;
      font-weight: bold;
      border-right: 1px solid #ddd;
    }

    .medications-table th:last-child {
      border-right: none;
    }

    .medications-table td {
      padding: 10px 6px;
      border-bottom: 1px solid #ddd;
      vertical-align: top;
    }

    .medications-table tbody tr:last-child td {
      border-bottom: 2px solid #000;
    }

    .med-number {
      width: 30px;
      text-align: center;
      font-weight: bold;
    }

    .med-name {
      min-width: 200px;
    }

    .med-name-primary {
      font-weight: 600;
      margin-bottom: 4px;
    }

    .med-gms-code {
      font-size: 9pt;
      color: #555;
      font-style: italic;
      margin-top: 2px;
    }

    .med-instructions {
      font-size: 9pt;
      color: #333;
      margin-top: 4px;
      font-style: italic;
    }

    .med-indication {
      font-size: 9pt;
      color: #666;
      margin-top: 2px;
    }

    .med-dose,
    .med-route,
    .med-frequency {
      min-width: 80px;
    }

    .med-quantity {
      min-width: 100px;
      font-weight: 500;
    }

    .instructions-content {
      padding: 10px;
      background-color: #f9f9f9;
      border-left: 3px solid #333;
      white-space: pre-wrap;
    }

    .repeat-info-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
    }

    .handwritten-instructions-section {
      page-break-inside: avoid;
    }

    .handwritten-instructions-box {
      border: 1px solid #333;
      padding: 10px 12px;
      min-height: 72pt;
      background: #fff;
    }

    .handwritten-instructions-prefill {
      font-size: 10pt;
      font-style: italic;
      color: #333;
      white-space: pre-wrap;
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px dashed #ccc;
    }

    .handwritten-instructions-lines {
      min-height: 56pt;
      background: repeating-linear-gradient(
        #fff,
        #fff 22px,
        #e8e8e8 22px,
        #e8e8e8 23px
      );
    }

    .prescriber-section {
      margin-top: 40px;
      page-break-inside: avoid;
    }

    .prescriber-signature-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 30px;
      margin-left: auto;
      max-width: 280px;
      text-align: center;
    }

    .prescriber-signature-area {
      width: 220px;
    }

    .prescriber-printed-details {
      margin-top: 14px;
      width: 100%;
    }

    .prescriber-name {
      font-size: 12pt;
      margin-bottom: 6px;
    }

    .prescriber-name strong {
      font-size: 12pt;
    }

    .prescriber-imc,
    .prescriber-gms {
      font-size: 10pt;
      color: #333;
      margin-top: 4px;
      line-height: 1.35;
    }

    .signature-line {
      border-bottom: 2px solid #000;
      height: 50px;
      margin-bottom: 5px;
      width: 100%;
    }

    .signature-label {
      font-size: 9pt;
      text-align: center;
      color: #666;
      margin-top: 5px;
    }

    .prescription-print-view .signature-image-container {
      width: 220px !important;
      min-height: 90px !important;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: visible;
      margin-bottom: 5px;
    }

    .prescription-print-view .signature-image-display {
      width: 220px !important;
      max-width: 220px !important;
      height: auto !important;
      max-height: 90px !important;
      object-fit: contain !important;
      object-position: center;
      display: block;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      background: transparent;
      box-sizing: border-box;
    }

    .prescription-footer {
      margin-top: 40px;
      padding-top: 15px;
      border-top: 1px solid #ddd;
      font-size: 9pt;
      color: #666;
      text-align: center;
    }

    .footer-note {
      margin: 5px 0;
    }

    @media print {
      .prescription-print-view {
        margin: 0;
        padding: 15mm;
        box-shadow: none;
      }

      .prescription-section {
        page-break-inside: avoid;
      }

      .medications-table {
        page-break-inside: auto;
      }

      .medications-table tbody tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }

      .medications-table thead {
        display: table-header-group;
      }

      .medications-table tfoot {
        display: table-footer-group;
      }

      .prescriber-section {
        page-break-inside: avoid;
        margin-top: 20px;
      }

      .no-print {
        display: none !important;
      }
    }

    @media screen {
      .prescription-print-view {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        margin: 20px auto;
      }
    }
  `
}

